import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, MapPin, Sparkles, Wrench, ScanEye, BadgeDollarSign, Heart, Check, Phone, Flame } from 'lucide-react';
import { IMG, FEATURES, HOW_IT_WORKS, TESTIMONIALS, PACKAGES, CONTACT, packagePrice } from '../data';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

const ICONS = { MapPin, Sparkles, Wrench, ScanEye, BadgeDollarSign, Heart };

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-[#0a0a0a] text-white">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <img src={IMG.hero} alt="Luxury car being detailed" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/70 via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-24 pt-32 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="pill mb-6"><Sparkles className="w-3 h-3" /> Mobile Detailing · Lincoln, NE · 3+ Years</span>
            <h1 className="font-display font-light tracking-tighter text-5xl sm:text-6xl lg:text-7xl max-w-4xl leading-[1.05]">
              Premium Mobile Detailing in <span className="text-blue-400">Lincoln, Nebraska</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-zinc-300 leading-relaxed">
              We bring professional detailing directly to your home or workplace — delivering showroom-quality results without the inconvenience of visiting a shop.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book" data-testid="hero-book-button" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-medium transition-all hover:shadow-[0_0_40px_rgba(37,99,235,0.5)] inline-flex items-center gap-2">
                Book Your Detail <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" data-testid="hero-pricing-button" className="border border-white/20 hover:bg-white/10 text-white px-8 py-4 rounded-full font-medium transition-all">
                View Pricing
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-8 text-sm text-zinc-400">
              <div><span className="text-3xl font-display font-light text-white">3+</span><br/>Years Detailing</div>
              <div><span className="text-3xl font-display font-light text-white">500+</span><br/>Vehicles Detailed</div>
              <div><span className="text-3xl font-display font-light text-white">5.0</span><br/>Average Rating</div>
              <div><span className="text-3xl font-display font-light text-white">100%</span><br/>Mobile Service</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <span className="pill mb-4">Why Choose Us</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight">Built for vehicles you take seriously.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = ICONS[f.icon];
              return (
                <motion.div
                  key={f.title}
                  data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, '-')}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="bg-[#141414] border border-white/5 rounded-2xl p-8 hover:-translate-y-1 hover:border-blue-500/40 transition-all duration-500"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="font-display text-2xl font-medium mb-2">{f.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-transparent to-[#080808]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <span className="pill mb-4">How It Works</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight">Four steps. Zero hassle.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 relative">
            {HOW_IT_WORKS.map((s, i) => (
              <motion.div
                key={s.step}
                data-testid={`how-step-${s.step}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative"
              >
                <div className="text-blue-400 font-display text-5xl font-light mb-4">{s.step}</div>
                <h3 className="font-display text-xl font-medium mb-2">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <span className="pill mb-4">Signature Packages</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight">Two packages. Endless detail.</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {Object.entries(PACKAGES).filter(([, p]) => p.category === 'Full Detail').map(([key, p]) => {
              const price = packagePrice(key, 'sedan');
              return (
                <div
                  key={key}
                  data-testid={`featured-${key}`}
                  className={`relative rounded-3xl overflow-hidden border ${key === 'premium' ? 'border-blue-500/40 bg-gradient-to-br from-blue-600/10 to-transparent shadow-[0_0_40px_rgba(37,99,235,0.15)]' : 'border-white/10 bg-[#141414]'}`}
                >
                  {price.promo && (
                    <div className="absolute top-4 right-4 z-10 bg-gradient-to-r from-amber-500 to-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                      <Flame className="w-3.5 h-3.5" /> Limited Time – 20% OFF <Flame className="w-3.5 h-3.5" />
                    </div>
                  )}
                  <img src={key === 'premium' ? IMG.luxury1 : IMG.regularDetail} alt={p.name} className="w-full h-64 object-cover" />
                  <div className="p-8">
                    {key === 'premium' && !price.promo && (
                      <span className="pill mb-3 !bg-blue-600 !text-white !border-blue-600">Most Popular</span>
                    )}
                    <h3 className="font-display text-3xl font-medium mb-2">{p.name}</h3>
                    <p className="text-zinc-400 mb-4">{p.tagline}</p>
                    <div className="flex items-baseline gap-2 mb-6">
                      <span className="font-display text-5xl font-light">${price.discounted}</span>
                      {price.promo && <span className="text-zinc-500 line-through">${price.original}</span>}
                      <span className="text-zinc-500 text-sm">starting · sedan / coupe</span>
                    </div>
                    <Link to="/services" className="text-blue-400 hover:text-blue-300 text-sm inline-flex items-center gap-2">
                      Learn More <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fleet card */}
          <div data-testid="featured-fleet" className="mt-6 rounded-3xl overflow-hidden border border-white/10 bg-[#141414] grid md:grid-cols-2">
            <img src={IMG.fleet} alt="Fleet detailing" className="w-full h-full object-cover min-h-[260px]" />
            <div className="p-8 md:p-10 flex flex-col justify-center">
              <span className="pill mb-3">Fleet Detailing</span>
              <h3 className="font-display text-3xl font-medium mb-2">Business Fleet Detailing</h3>
              <p className="text-zinc-400 mb-5">Multiple company cars, work trucks, or service vehicles? We&apos;ll keep your whole fleet looking sharp. Custom-quoted based on fleet size and frequency.</p>
              <div className="flex flex-wrap gap-3">
                <a href={`tel:${CONTACT.fleetPhoneRaw}`} data-testid="fleet-call" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium inline-flex items-center gap-2"><Phone className="w-4 h-4" /> Call for Quote</a>
                <a href={`sms:${CONTACT.fleetPhoneRaw}`} data-testid="fleet-text" className="border border-white/20 hover:bg-white/10 text-white px-6 py-3 rounded-full font-medium">Text {CONTACT.fleetPhone}</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE AFTER */}
      <section className="py-24 md:py-32 bg-[#080808]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-12">
            <span className="pill mb-4">Real Results</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight">Drag to see the difference.</h2>
          </div>
          <BeforeAfterSlider before={IMG.beforeWash} after={IMG.afterWash} alt="Interior detailing transformation" />

          <div className="mt-16 max-w-2xl">
            <span className="pill mb-4">Boats Too</span>
            <h3 className="font-display font-light text-3xl sm:text-4xl tracking-tight">From dusty to dialed-in.</h3>
            <p className="text-zinc-400 mt-3">We detail boats, semi trucks, and everything in between.</p>
          </div>
          <div className="mt-8">
            <BeforeAfterSlider before={IMG.boatBefore} after={IMG.boatAfter} alt="Boat detailing transformation" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <span className="pill mb-4">Customer Stories</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight">Trusted by Lincoln drivers.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-[#141414] border border-white/5 rounded-2xl p-8"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-blue-400 text-blue-400" />)}
                </div>
                <p className="text-zinc-300 leading-relaxed mb-6">&ldquo;{t.text}&rdquo;</p>
                <div className="text-sm">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-zinc-500">{t.role}</div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Leave a Google Review + Nextdoor */}
          <div className="mt-12 rounded-3xl border border-blue-500/30 bg-gradient-to-br from-blue-600/10 to-[#141414] p-8 md:p-12 text-center">
            <span className="pill mb-3">Loved Your Detail?</span>
            <h3 className="font-display text-2xl md:text-3xl font-medium mb-2">Leave us a review.</h3>
            <p className="text-zinc-400 text-sm max-w-xl mx-auto mb-8">Your feedback helps other drivers in Lincoln find us and helps us keep raising the bar.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href={CONTACT.googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-review-button"
                className="bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2 hover:shadow-[0_0_30px_rgba(37,99,235,0.4)]"
              >
                <Star className="w-4 h-4 fill-white" /> Leave a Google Review
              </a>
              <a
                href="https://nextdoor.com/page/express-car-detailing-lincoln-ne"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nextdoor-review-button"
                className="border border-white/20 hover:bg-white/10 text-white px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2"
              >
                <Star className="w-4 h-4" /> Read Our Nextdoor Reviews
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="relative rounded-3xl overflow-hidden border border-blue-500/30 bg-gradient-to-br from-blue-600/20 via-[#0a0a0a] to-[#0a0a0a] p-12 md:p-20 text-center">
            <span className="pill mb-6">Ready When You Are</span>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl mx-auto">
              Schedule your detail today.
            </h2>
            <p className="text-zinc-300 mt-5 max-w-xl mx-auto">
              Reserve your spot in under a minute. We&apos;ll come to you — anywhere in Lincoln and surrounding areas.
            </p>
            <Link to="/book" data-testid="final-cta-book" className="mt-8 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-medium transition-all hover:shadow-[0_0_40px_rgba(37,99,235,0.5)]">
              Book Your Detail <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
